/**
 * France Travail Declaration Controller (EPIC-020 / 20.6 — §10.B).
 *
 * Page : `/france-travail-declaration.html`. Pilote la déclaration
 * mensuelle France Travail des bénéficiaires de l'ARE :
 *   - chargement du calculateur du mois courant (GET …/current-month)
 *     → revenu forfaitaire = Σ CA_cat × (1 − abattement_cat)
 *   - navigation mois précédent / suivant (rebascule sur GET)
 *   - copie du montant calculé dans le presse-papiers
 *   - création / édition d'une déclaration (POST + PATCH)
 *   - historique paginé via `<template>` + `data-slot`
 *
 * Conventions :
 *   - aucun HTML inline en JS : `<template id="france-travail-row-template">`
 *     + `data-slot` (cf. `utils/template.js`).
 *   - les erreurs 403 (`not_eligible`) sont affichées en toast et la
 *     page reste accessible (les boutons sont désactivés).
 *   - les montants sont formatés en FR (`fr-FR`) à l'affichage et
 *     parsés en `Number` à l'envoi (le backend stocke en décimal).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "../services/api.js";

const MONTH_NAMES_LONG = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

const MONTH_NAMES_SHORT = [
  "janv.", "févr.", "mars", "avr.", "mai", "juin",
  "juil.", "août", "sept.", "oct.", "nov.", "déc.",
];

const EURO_FORMATTER = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const HISTORY_PAGE_SIZE = 10;

export default class FranceTravailController extends BaseController {
  static targets = [
    "monthTitle",
    "deadlineLabel",
    "prevMonthBtn",
    "nextMonthBtn",
    "caMonth",
    "abattementRate",
    "revenuForfaitaire",
    "formulaCa",
    "formulaAbattement",
    "declarerForm",
    "declaredAtInput",
    "declaredAmountInput",
    "areReceivedInput",
    "notesInput",
    "historyTbody",
    "historyCount",
    "rowTemplate",
  ];

  connect() {
    this._currentMonth = this._todayMonthYear();
    this._editingId = null;
    this._lastComputedAmount = "0.00";
    this.loadCalculateur();
    this.loadHistory();
  }

  // ---------------------------------------------------------------------------
  // Calculateur du mois
  // ---------------------------------------------------------------------------

  async loadCalculateur() {
    try {
      const response = await api.franceTravail.getCurrentMonth(this._currentMonth);
      this._applyComputeToUI(response.data);
    } catch (err) {
      this._handleError(err, "Impossible de charger le calculateur.");
    }
  }

  _applyComputeToUI(data) {
    const monthStart = this._parseMonthYear(data.monthYear);
    const monthIndex = monthStart.getMonth();
    const year = monthStart.getFullYear();
    const longLabel = `${this._capitalize(MONTH_NAMES_LONG[monthIndex])} ${year}`;

    if (this.hasMonthTitleTarget) {
      this.monthTitleTarget.textContent = longLabel;
    }
    if (this.hasPrevMonthBtnTarget) {
      const prev = this._shiftMonth(monthStart, -1);
      this.prevMonthBtnTarget.textContent =
        `← ${this._capitalize(MONTH_NAMES_LONG[prev.getMonth()])} ${prev.getFullYear()}`;
    }
    if (this.hasNextMonthBtnTarget) {
      const next = this._shiftMonth(monthStart, 1);
      this.nextMonthBtnTarget.textContent =
        `${this._capitalize(MONTH_NAMES_LONG[next.getMonth()])} ${next.getFullYear()} →`;
    }
    if (this.hasDeadlineLabelTarget) {
      const deadlineMonth = this._shiftMonth(monthStart, 1);
      this.deadlineLabelTarget.textContent =
        `5 ${MONTH_NAMES_LONG[deadlineMonth.getMonth()]} ${deadlineMonth.getFullYear()}`;
    }

    const caTotal = Object.values(data.caByCategory)
      .reduce((acc, v) => acc + Number(v || 0), 0);
    if (this.hasCaMonthTarget) {
      this.caMonthTarget.value = EURO_FORMATTER.format(caTotal);
    }

    const revenu = Number(data.revenuForfaitaire || 0);
    this._lastComputedAmount = data.revenuForfaitaire;

    const abattementPct = caTotal > 0
      ? Math.round((1 - revenu / caTotal) * 100)
      : null;
    if (this.hasAbattementRateTarget) {
      this.abattementRateTarget.value = abattementPct !== null ? `${abattementPct} %` : "";
    }
    if (this.hasFormulaCaTarget) {
      this.formulaCaTarget.textContent = `${EURO_FORMATTER.format(caTotal)} €`;
    }
    if (this.hasFormulaAbattementTarget) {
      this.formulaAbattementTarget.textContent = abattementPct !== null ? `${abattementPct} %` : "—";
    }
    if (this.hasRevenuForfaitaireTarget) {
      const integer = Math.trunc(revenu);
      const decimals = (revenu - integer).toFixed(2).slice(2);
      const amountText =
        `${EURO_FORMATTER.format(integer).replace(/,00$/, "")}${decimals !== "00" ? "," + decimals : ""} `;
      const euroSpan = document.createElement("span");
      euroSpan.className = "fs-24";
      euroSpan.textContent = "€";
      this.revenuForfaitaireTarget.replaceChildren(
        document.createTextNode(amountText),
        euroSpan,
      );
    }

    if (this.hasDeclaredAmountInputTarget && this._editingId === null) {
      this.declaredAmountInputTarget.value = EURO_FORMATTER.format(revenu);
    }
  }

  prevMonth() {
    const cursor = this._parseMonthYear(this._currentMonth);
    this._currentMonth = this._formatMonthYear(this._shiftMonth(cursor, -1));
    this.loadCalculateur();
  }

  nextMonth() {
    const cursor = this._parseMonthYear(this._currentMonth);
    this._currentMonth = this._formatMonthYear(this._shiftMonth(cursor, 1));
    this.loadCalculateur();
  }

  // ---------------------------------------------------------------------------
  // Bouton « Copier le montant »
  // ---------------------------------------------------------------------------

  async copyMontant() {
    const amount = Number(this._lastComputedAmount || 0);
    const text = amount.toFixed(2);
    try {
      await navigator.clipboard.writeText(text);
      this.showSuccess(`Montant copié : ${EURO_FORMATTER.format(amount)} €`);
    } catch {
      this.showError("Impossible de copier dans le presse-papiers.");
    }
  }

  // ---------------------------------------------------------------------------
  // Formulaire de déclaration
  // ---------------------------------------------------------------------------

  cancelForm() {
    this._editingId = null;
    if (this.hasDeclarerFormTarget) {
      this.declarerFormTarget.reset();
    }
    this.loadCalculateur();
  }

  async submitDeclaration(event) {
    event.preventDefault();

    const declaredAt = this.declaredAtInputTarget.value;
    const declaredAmount = this._parseAmount(this.declaredAmountInputTarget.value);
    const areReceivedRaw = this.areReceivedInputTarget.value.trim();
    const areReceived = areReceivedRaw === "" ? null : this._parseAmount(areReceivedRaw);
    const notesRaw = this.notesInputTarget.value.trim();
    const notes = notesRaw === "" ? null : notesRaw;

    if (!declaredAt || Number.isNaN(declaredAmount)) {
      this.showError("Date et montant déclaré sont obligatoires.");
      return;
    }

    try {
      if (this._editingId) {
        await api.franceTravail.update(this._editingId, {
          declaredAt,
          declaredAmount,
          areReceived,
          notes,
        });
        this.showSuccess("Déclaration mise à jour.");
        this._editingId = null;
      } else {
        await api.franceTravail.create({
          monthYear: this._currentMonth,
          declaredAt,
          declaredAmount,
          areReceived,
          notes,
        });
        this.showSuccess("Déclaration enregistrée.");
      }
      this.loadHistory();
      this.loadCalculateur();
    } catch (err) {
      if (err instanceof APIError && err.code === "conflict") {
        const existingId = err.details?.existingId;
        this.showError(
          "Une déclaration existe déjà pour ce mois. Cliquez sur Modifier dans l'historique.",
        );
        if (existingId) {
          this._editingId = existingId;
        }
        return;
      }
      this._handleError(err, "Impossible d'enregistrer la déclaration.");
    }
  }

  // ---------------------------------------------------------------------------
  // Historique
  // ---------------------------------------------------------------------------

  async loadHistory(page = 1) {
    try {
      const response = await api.franceTravail.listDeclarations(page, HISTORY_PAGE_SIZE);
      this._renderHistory(response.data?.data ?? []);
      this._renderHistoryCount(response.data?.pagination?.total ?? response.data?.data?.length ?? 0);
    } catch (err) {
      this._handleError(err, "Impossible de charger l'historique.");
    }
  }

  _renderHistoryCount(total) {
    if (!this.hasHistoryCountTarget) {
      return;
    }
    if (total === 0) {
      this.historyCountTarget.textContent = "Aucune déclaration enregistrée";
    } else if (total === 1) {
      this.historyCountTarget.textContent = "1 déclaration enregistrée";
    } else {
      this.historyCountTarget.textContent = `${total} déclarations enregistrées`;
    }
  }

  _renderHistory(rows) {
    if (!this.hasHistoryTbodyTarget) {
      return;
    }
    this.historyTbodyTarget.replaceChildren();

    for (const row of rows) {
      const fragment = this.cloneTemplate("france-travail-row-template");
      const monthLabel = this._formatLongMonth(row.monthYear);
      this.fillSlots(fragment, {
        monthYear: monthLabel,
        declaredAt: this._formatShortDateFr(row.declaredAt),
        declaredAmount: `${EURO_FORMATTER.format(Number(row.declaredAmount))} €`,
        areReceived: row.areReceived !== null && row.areReceived !== undefined
          ? `${EURO_FORMATTER.format(Number(row.areReceived))} €`
          : "—",
      });

      const link = fragment.querySelector('[data-slot="actions"] a');
      if (link) {
        link.setAttribute("href", "#");
        link.dataset.action = "click->france-travail#openEditForm";
        link.dataset.id = row.id;
        link.dataset.monthYear = row.monthYear;
      }

      this.historyTbodyTarget.appendChild(fragment);
    }
  }

  openEditForm(event) {
    event.preventDefault();
    const link = event.currentTarget;
    const id = link.dataset.id;
    const monthYear = link.dataset.monthYear;
    if (!id || !monthYear) {
      return;
    }
    this._editingId = id;
    this._currentMonth = monthYear;
    this.loadCalculateur();
    this.declarerFormTarget?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _todayMonthYear() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }

  _parseMonthYear(monthYear) {
    const [year, month] = monthYear.split("-").map(Number);
    return new Date(year, month - 1, 1);
  }

  _formatMonthYear(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  }

  _shiftMonth(date, delta) {
    return new Date(date.getFullYear(), date.getMonth() + delta, 1);
  }

  _formatLongMonth(monthYear) {
    const d = this._parseMonthYear(monthYear);
    return `${this._capitalize(MONTH_NAMES_LONG[d.getMonth()])} ${d.getFullYear()}`;
  }

  _formatShortDateFr(isoDate) {
    if (!isoDate) {
      return "—";
    }
    const d = new Date(isoDate);
    return `${d.getDate()} ${MONTH_NAMES_SHORT[d.getMonth()]} ${d.getFullYear()}`;
  }

  _capitalize(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  _parseAmount(value) {
    if (typeof value !== "string") {
      return Number(value);
    }
    const normalized = value.replace(/\s/g, "").replace(",", ".");
    return Number(normalized);
  }

  _handleError(err, fallbackMessage) {
    if (err instanceof APIError) {
      if (err.code === "forbidden") {
        this.showError(
          "Fonctionnalité réservée aux micro-entrepreneurs bénéficiant de l'ARE.",
        );
        return;
      }
      this.showError(err.message || fallbackMessage);
      return;
    }
    this.showError(fallbackMessage);
  }
}
